// src/pages/ProfilePage.jsx
// ─────────────────────────────────────────────────────────────────
//  Premium ProfilePage — 24News design system
//  No Bootstrap. Full logic from original preserved.
//  RTL · Dark mode · Responsive · Accessible · i18n
// ─────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "../i18n";   // ← i18n added
import { Helmet } from "react-helmet-async";

/* ═══════════════════════════════════════════════════════════════
   CONFIG & HELPERS
═══════════════════════════════════════════════════════════════ */
const API_BASE       = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";
const PROFILE_URL    = `${API_BASE}/accounts/userprofile/me/`;
const CHANGE_PW_URL  = `${API_BASE}/auth/users/set_password/`;
const AUTHORED_URL   = (u) => `${API_BASE}/authors/${encodeURIComponent(u)}/articles/`;

const getToken = () => {
  try {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("token") ||
      null
    );
  } catch { return null; }
};

const authHeader = (t) =>
  t ? { Authorization: t.split(".").length >= 3 ? `Bearer ${t}` : `Token ${t}` } : {};

function toAbsoluteAvatarUrl(candidate) {
  if (!candidate || typeof candidate !== "string") return null;
  candidate = candidate.trim();
  if (!candidate) return null;
  if (/^data:/i.test(candidate) || /^https?:\/\//i.test(candidate)) return candidate;
  try {
    const origin = new URL(API_BASE).origin || window.location.origin;
    return origin + (candidate.startsWith("/") ? "" : "/") + candidate;
  } catch {
    return window.location.origin + (candidate.startsWith("/") ? "" : "/") + candidate;
  }
}

/* ═══════════════════════════════════════════════════════════════
   HOOKS
═══════════════════════════════════════════════════════════════ */
function useAnimatedNumber(target, duration = 700) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    let start = null;
    const to = Number(target || 0);
    if (to === 0) { setValue(0); return; }
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      setValue(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);
  return value;
}

/* ═══════════════════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════════════════ */
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div className="pp-toast-wrap" role="alert" aria-live="assertive">
      <div className={`pp-toast pp-toast--${ok ? "ok" : "err"}`}>
        <i className={`bi ${ok ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`} aria-hidden />
        <span>{toast.message}</span>
        <button type="button" className="pp-toast-close" onClick={onClose} aria-label="Dismiss">
          <i className="bi bi-x" />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CONFIRM MODAL
═══════════════════════════════════════════════════════════════ */
function ConfirmModal({ show, title, body, onCancel, onConfirm, confirmText = "Confirm", cancelText = "Cancel", busy = false, danger = false }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && show) onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [show, onCancel]);

  if (!show) return null;
  return (
    <div className="pp-backdrop" onClick={onCancel}>
      <div
        className="pp-modal"
        role="dialog" aria-modal="true" aria-labelledby="pp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pp-modal-head">
          <h2 className="pp-modal-title" id="pp-modal-title">{title}</h2>
          <button type="button" className="pp-icon-btn" onClick={onCancel} aria-label="Close"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="pp-modal-body">
          {typeof body === "string"
            ? <p className="pp-modal-text">{body}</p>
            : body}
        </div>
        <div className="pp-modal-foot">
          <button type="button" className="pp-btn pp-btn--ghost" onClick={onCancel} disabled={busy}>{cancelText}</button>
          <button type="button" className={`pp-btn ${danger ? "pp-btn--danger" : "pp-btn--solid"}`} onClick={onConfirm} disabled={busy}>
            {busy ? <span className="pp-spinner" /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════ */
function Skeleton() {
  return (
    <div className="pp-root">
      <ProfileStyles />
      <div className="pp-cover"><div className="pp-cover-inner" /></div>
      <div className="pp-container">
        <div className="pp-hero-card">
          <div className="pp-hero-accent" aria-hidden />
          <div className="pp-hero-body">
            <div className="pp-sk pp-sk--avatar" />
            <div style={{ flex: 1 }}>
              <div className="pp-sk" style={{ width: 200, height: 24, marginBottom: 10 }} />
              <div className="pp-sk" style={{ width: 140, height: 14, marginBottom: 20 }} />
              <div style={{ display: "flex", gap: 20 }}>
                {[1, 2, 3].map(i => (
                  <div key={i}>
                    <div className="pp-sk" style={{ width: 36, height: 22, marginBottom: 4 }} />
                    <div className="pp-sk" style={{ width: 50, height: 12 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="pp-layout">
          <div>
            {[200, 280, 200].map((h, i) => (
              <div key={i} className="pp-card" style={{ height: h, marginBottom: 16 }}>
                <div className="pp-sk" style={{ width: "100%", height: "100%", borderRadius: 10 }} />
              </div>
            ))}
          </div>
          <aside>
            <div className="pp-card" style={{ height: 300 }}>
              <div className="pp-sk" style={{ width: "100%", height: "100%", borderRadius: 10 }} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function ProfilePage({ creatorMode = false }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();          // ← i18n
  const isRTL = lang === "ar";                   // ← RTL flag

  /* ── state ── */
  const [user,           setUser]           = useState(null);
  const [savedSubjects,  setSavedSubjects]  = useState([]);
  const [articles,       setArticles]       = useState([]);
  const [activity,       setActivity]       = useState([]);

  const [loading,          setLoading]          = useState(true);
  const [loadingSubjects,  setLoadingSubjects]  = useState(true);
  const [loadingArticles,  setLoadingArticles]  = useState(true);

  const [toast,       setToast]       = useState(null);
  const toastTimer                   = useRef(null);

  const [activeTab,   setActiveTab]   = useState("activity");
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [savingEdit,  setSavingEdit]  = useState(false);

  const [editForm, setEditForm] = useState({
    username: "", email: "", first_name: "", last_name: "",
    language: localStorage.getItem("ui_language") || "en",
    avatarFile: null, avatarPreview: null,
  });

  const [editPwOpen,    setEditPwOpen]    = useState(false);
  const [editPwForm,    setEditPwForm]    = useState({ current_password: "", new_password: "", re_new_password: "" });
  const [editPwLoading, setEditPwLoading] = useState(false);

  const [pwOpen,    setPwOpen]    = useState(false);
  const [pwForm,    setPwForm]    = useState({ new_password: "", re_new_password: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const [deleteOpen,    setDeleteOpen]    = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [avatarSrc, setAvatarSrc] = useState(null);
  const [showTop,   setShowTop]   = useState(false);
  const [mounted,   setMounted]   = useState(false);

  const previewRef = useRef(null);

  /* animated stats */
  const savedCount    = useAnimatedNumber(savedSubjects.length);
  const articlesCount = useAnimatedNumber(articles.length);
  const activityCount = useAnimatedNumber(activity.length);

  /* ── toast ── */
  const showToast = useCallback((message, type = "success", ms = 3800) => {
    setToast({ message, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);

  /* ── scroll watcher + mount ── */
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 80);
    const fn = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", fn, { passive: true });
    return () => { clearTimeout(id); window.removeEventListener("scroll", fn); };
  }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (previewRef.current) { try { URL.revokeObjectURL(previewRef.current); } catch {} }
  }, []);

  /* ── fetch profile ── */
  useEffect(() => {
    let mounted = true;
    const token = getToken();
    if (!token) { navigate("/login"); return; }

    (async () => {
      setLoading(true);
      try {
        const meRes = await axios.get(PROFILE_URL, { headers: authHeader(token) });
        if (!mounted) return;
        const me = meRes.data || {};
        setUser(me);
        setEditForm(f => ({
          ...f,
          username: me.username || "", email: me.email || "",
          first_name: me.first_name || "", last_name: me.last_name || "",
          avatarFile: null, avatarPreview: null,
        }));

        const candidate = me.avatar || me.profile_image || me.image || me.avatar_url || me.profile?.avatar || null;
        setAvatarSrc(toAbsoluteAvatarUrl(candidate));

        /* saved subjects — try multiple endpoints */
        const subUrls = [
          `${API_BASE}/users/${encodeURIComponent(me.username)}/saved_subjects/`,
          `${API_BASE}/me/saved_subjects/`,
          `${API_BASE}/subjects/saved/`,
        ];
        for (const u of subUrls) {
          try {
            const r = await axios.get(u, { headers: authHeader(token) });
            const d = Array.isArray(r.data) ? r.data : r.data?.results || [];
            if (mounted) { setSavedSubjects(d); setLoadingSubjects(false); break; }
          } catch {}
        }
        if (mounted) setLoadingSubjects(false);

        const isCreator = !!(creatorMode || me.is_creator || me.is_author || me.is_staff || me.profile?.is_creator);

if (isCreator) {
  try {
    const ar = await axios.get(AUTHORED_URL(me.username), { headers: authHeader(token) });
    if (mounted) {
      setArticles(Array.isArray(ar.data) ? ar.data : ar.data?.results || []);
      setLoadingArticles(false);
    }
  } catch {
    if (mounted) setLoadingArticles(false);
  }
} else {
  if (mounted) setLoadingArticles(false);
}

try {
  const actParams  = { lang, language: lang };
const actHeaders = {
  ...authHeader(token),
  "Accept-Language": lang,          // ← هذا يفعّل gettext في Django
  "X-Language":      lang,
};

  // try the standard URL first, then fallbacks
  const actUrls = [
  `${API_BASE}/accounts/${encodeURIComponent(me.username)}/activity/`,
  `${API_BASE}/users/${encodeURIComponent(me.username)}/activity/`,
  `${API_BASE}/user-activity/`,
  `${API_BASE}/activity/`,
];

  let actData = null;
  for (const actUrl of actUrls) {
    try {
      const av = await axios.get(actUrl, {
        headers: actHeaders,
        params:  actParams,
      });
      actData = Array.isArray(av.data) ? av.data : av.data?.results || [];
      break; // success — stop trying
    } catch (urlErr) {
      if (urlErr?.response?.status === 404) continue; // try next
      if (urlErr?.response?.status === 401) break;    // auth error — stop
      // Network error on this URL — try next
console.warn("activity URL failed:", actUrl, urlErr?.message);
continue;    }
  }

  if (actData && mounted) setActivity(actData);
} catch (err) {
  console.error("activity fetch failed:", err);
}
      } catch {
        localStorage.removeItem("accessToken"); localStorage.removeItem("refreshToken");
        if (mounted) navigate("/login");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [creatorMode, navigate]);

  /* ── avatar file handling ── */
  const handleAvatarFile = useCallback((file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { showToast(t("avatar_too_large") || "Image too large — max 6 MB", "danger"); return; }
    const ok = (file.type && file.type.startsWith("image/")) || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.name || "");
    if (!ok) { showToast(t("avatar_invalid_type") || "Please pick an image file (JPG, PNG, WEBP…)", "danger"); return; }
    if (previewRef.current) { try { URL.revokeObjectURL(previewRef.current); } catch {} }
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setEditForm(f => ({ ...f, avatarFile: file, avatarPreview: url }));
    setAvatarSrc(url);
  }, [showToast, t]);

  const closeDrawer = useCallback(() => {
    if (previewRef.current) { try { URL.revokeObjectURL(previewRef.current); } catch {} previewRef.current = null; }
    setEditForm(f => ({ ...f, avatarFile: null, avatarPreview: null }));
    setDrawerOpen(false);
    setEditPwOpen(false);
  }, []);

  /* ── submit profile edit ── */
  const submitProfileEdit = async (e) => {
    e?.preventDefault();
    const token = getToken();
    if (!token) { showToast(t("not_authenticated") || "Not authenticated", "danger"); return; }
    const oldUser = user ? { ...user } : null;
    setUser(prev => ({ ...prev, username: editForm.username, email: editForm.email, first_name: editForm.first_name, last_name: editForm.last_name }));
    setSavingEdit(true);
    try {
      const fd = new FormData();
      fd.append("username",   editForm.username || "");
      fd.append("email",      editForm.email    || "");
      fd.append("first_name", editForm.first_name || "");
      fd.append("last_name",  editForm.last_name  || "");
      if (editForm.avatarFile) { fd.append("profile_image", editForm.avatarFile); fd.append("avatar", editForm.avatarFile); }
      const res = await axios.patch(PROFILE_URL, fd, { headers: { ...authHeader(token) } });
      setUser(res.data);
      const sc = res.data?.avatar || res.data?.profile_image || res.data?.avatar_url || null;
      if (previewRef.current) { try { URL.revokeObjectURL(previewRef.current); } catch {} previewRef.current = null; }
      setEditForm(f => ({ ...f, avatarFile: null, avatarPreview: null }));
      if (sc) setAvatarSrc(toAbsoluteAvatarUrl(sc));
      showToast(t("profile_updated") || "Profile updated successfully", "success");
      setDrawerOpen(false);
    } catch (err) {
      if (oldUser) setUser(oldUser);
      showToast(`${t("update_failed") || "Update failed"}: ${err?.response?.data?.detail || err?.message || "Unknown error"}`, "danger");
    } finally { setSavingEdit(false); }
  };

  /* ── password (modal) ── */
  const submitChangePw = async () => {
    if (!pwForm.new_password || pwForm.new_password !== pwForm.re_new_password) {
      showToast(t("passwords_must_match") || "Passwords must match", "danger"); return;
    }
    const token = getToken();
    if (!token) { showToast(t("not_authenticated") || "Not authenticated", "danger"); return; }
    setPwLoading(true);
    try {
      await axios.post(CHANGE_PW_URL, pwForm, { headers: authHeader(token) });
      showToast(t("password_changed") || "Password changed", "success");
      setPwOpen(false); setPwForm({ new_password: "", re_new_password: "" });
    } catch (err) {
      showToast(`${t("password_change_failed") || "Password change failed"}: ${err?.response?.data?.detail || err?.message}`, "danger");
    } finally { setPwLoading(false); }
  };

  /* ── password (drawer) ── */
  const submitDrawerPw = async () => {
    if (!editPwForm.new_password || editPwForm.new_password !== editPwForm.re_new_password) {
      showToast(t("passwords_must_match") || "Passwords must match", "danger"); return;
    }
    const token = getToken();
    if (!token) { showToast(t("not_authenticated") || "Not authenticated", "danger"); return; }
    setEditPwLoading(true);
    try {
      const payload = { new_password: editPwForm.new_password, re_new_password: editPwForm.re_new_password };
      if (editPwForm.current_password) payload.current_password = editPwForm.current_password;
      await axios.post(CHANGE_PW_URL, payload, { headers: authHeader(token) });
      showToast(t("password_changed") || "Password changed", "success");
      setEditPwForm({ current_password: "", new_password: "", re_new_password: "" });
      setEditPwOpen(false);
    } catch (err) {
      showToast(`${t("password_change_failed") || "Password change failed"}: ${err?.response?.data?.detail || err?.message}`, "danger");
    } finally { setEditPwLoading(false); }
  };

  const refreshProfile = async (announce = true) => {
    const token = getToken();
    if (!token) return;
    try {
      const r = await axios.get(PROFILE_URL, { headers: authHeader(token) });
      const me = r.data || {};
      setUser(me);
      setAvatarSrc(toAbsoluteAvatarUrl(me.avatar || me.profile_image || me.avatar_url || me.profile?.avatar || null));
      if (announce) showToast(t("profile_refreshed") || "Profile refreshed", "success");
    } catch { if (announce) showToast(t("refresh_failed") || "Refresh failed", "danger"); }
  };

  const copyLink = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/@${user.username}`);
      showToast(t("link_copied") || "Profile link copied!", "success");
    } catch { showToast(t("copy_failed") || "Copy failed", "danger"); }
  };

  const confirmDelete = async () => {
    const token = getToken();
    if (!token) { showToast(t("not_authenticated") || "Not authenticated", "danger"); return; }
    setDeleteLoading(true);
    try {
      await axios.delete(PROFILE_URL, { headers: authHeader(token) });
      localStorage.removeItem("accessToken"); localStorage.removeItem("refreshToken"); sessionStorage.removeItem("accessToken");
      showToast(t("account_deleted") || "Account deleted", "success");
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (err) {
      showToast(`${t("delete_failed") || "Delete failed"}: ${err?.response?.data?.detail || err?.message}`, "danger");
    } finally { setDeleteLoading(false); setDeleteOpen(false); }
  };

  const exportArticles = () => {
    try {
      const blob = new Blob([JSON.stringify(articles, null, 2)], { type: "application/json" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `${user?.username || "articles"}-articles.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      showToast(t("articles_exported") || "Articles exported", "success");
    } catch { showToast(t("export_failed") || "Export failed", "danger"); }
  };
/* ── delete a comment directly from activity ── */
const deleteActivityComment = useCallback(async (commentId, articleSlug, activityId) => {
  if (!commentId) return;
  if (!window.confirm(t("confirm_delete_comment") || "Delete this comment?")) return;
  const token = getToken();
  if (!token) { showToast(t("not_authenticated") || "Not authenticated", "danger"); return; }
  try {
    // Try article comment endpoint first
    const urls = [
      articleSlug ? `${API_BASE}/articles/${articleSlug}/comments/${commentId}/` : null,
      `${API_BASE}/comments/${commentId}/`,
      `${API_BASE}/subjects/comments/${commentId}/`,
    ].filter(Boolean);

    let deleted = false;
    for (const url of urls) {
      try {
        const res = await axios.delete(url, { headers: authHeader(token) });
        if (res.status === 204 || res.status === 200) { deleted = true; break; }
      } catch {}
    }

    if (deleted) {
      setActivity(prev => prev.filter(it => String(it.id) !== String(activityId)));

      showToast(t("comment_deleted") || "Comment deleted", "success");
    } else {
      showToast(t("delete_failed") || "Delete failed", "danger");
    }
  } catch (err) {
    showToast(`${t("delete_failed") || "Delete failed"}: ${err?.message}`, "danger");
  }
}, [showToast, t]);
  /* ── derived ── */
  const initials = useMemo(() => {
    if (!user) return "U";
    if (user.first_name || user.last_name) return `${(user.first_name || " ")[0]}${(user.last_name || " ")[0]}`.toUpperCase();
    return (user.username || "U").slice(0, 2).toUpperCase();
  }, [user]);

  /* ── locale for date formatting ── */
  const dateLocale = useMemo(() => {
    const map = { ar: "ar", fr: "fr", de: "de", en: "en" };
    return map[lang] || "en";
  }, [lang]);

  /* ─────────── early returns ─────────── */
  if (loading) return <Skeleton />;
  if (!user)   return null;

  const isCreator   = !!(creatorMode || user.is_creator || user.is_author || user.is_staff || user.profile?.is_creator);
  const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;

  const TABS = [
    { key: "activity", icon: "bi-lightning-charge", label: t("activity") || "Activity",  count: activityCount },
    { key: "saved",    icon: "bi-bookmark-heart",   label: t("saved")    || "Saved",     count: savedCount    },
    ...(isCreator ? [{ key: "articles", icon: "bi-newspaper", label: t("articles") || "Articles", count: articlesCount }] : []),
  ];

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */

  function GridCard({ subject }) {
    const getLocalizedDesc = (subject) => {
      const desc = subject.description;
      if (!desc) return null;

      if (typeof desc === 'string') return desc;

      if (typeof desc === 'object') {
        // Exact match (e.g., 'fr')
        if (desc[lang]) return desc[lang];

        // Try prefix match (e.g., 'fr' from 'fr-FR')
        const langPrefix = lang.split('-')[0].split('_')[0];
        for (const key in desc) {
          if (key.startsWith(langPrefix)) return desc[key];
        }

        // Fallback to English
        if (desc.en) return desc.en;

        // Last resort
        return Object.values(desc)[0];
      }

      return null;
    };

    const descText = getLocalizedDesc(subject);
    const truncatedDesc = descText && descText.length > 110
      ? descText.slice(0, 110) + '…'
      : descText;

    return (
      <div className="pp-subject-card" dir={isRTL ? "rtl" : "ltr"}>
        <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
        <div className="pp-subject-top">
          <Link to={`/subject/${subject.slug || subject.id}`} className="pp-subject-name">
            {subject.name}
          </Link>
          {descText && (
            <p className="pp-subject-desc">{truncatedDesc}</p>
          )}
        </div>
        <div className="pp-subject-foot">
          <Link to={`/subject/${subject.slug || subject.id}`} className="pp-subject-link">
            {t("open") || "Open"} <i className="bi bi-arrow-right" aria-hidden />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`pp-root${mounted ? " pp-in" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <ProfileStyles />
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── COVER ── */}
      <div className="pp-cover" aria-hidden>
        <div className="pp-cover-inner" />
        <div className="pp-cover-dots" />
      </div>

      <div className="pp-container">

        {/* ══════════════ HERO CARD ══════════════ */}
        <div className="pp-hero-card">
          <div className="pp-hero-accent" aria-hidden />

          <div className="pp-hero-body">
            {/* ── Avatar ── */}
            <div className="pp-avatar-wrap">
              {(editForm.avatarPreview || avatarSrc) ? (
                <img
                  src={editForm.avatarPreview || avatarSrc}
                  alt={displayName}
                  className="pp-avatar-img"
                  onError={() => setAvatarSrc(null)}
                />
              ) : (
                <div className="pp-avatar-letters">{initials}</div>
              )}
              <button
                type="button"
                className="pp-avatar-cam"
                onClick={() => setDrawerOpen(true)}
                aria-label={t("change_photo") || "Change photo"}
              >
                <i className="bi bi-camera-fill" />
              </button>
            </div>

            {/* ── Identity ── */}
            <div className="pp-hero-info">
              <div className="pp-hero-top-row">
                <div className="pp-hero-identity">
                  <h1 className="pp-display-name">{displayName}</h1>
                  <div className="pp-meta-row">
                    <span className="pp-at">@{user.username}</span>
                    {user.is_staff && (
                      <span className="pp-badge pp-badge--red">
                        <i className="bi bi-shield-check" aria-hidden />{t("staff") || "Staff"}
                      </span>
                    )}
                    {isCreator && !user.is_staff && (
                      <span className="pp-badge pp-badge--green">
                        <i className="bi bi-pencil-square" aria-hidden />{t("creator") || "Creator"}
                      </span>
                    )}
                    {user.email && (
                      <span className="pp-email-chip">
                        <i className="bi bi-envelope" aria-hidden />{user.email}
                      </span>
                    )}
                  </div>
                  {user.date_joined && (
                    <div className="pp-joined">
                      <i className="bi bi-calendar3" aria-hidden />
                      {t("joined") || "Joined"}{" "}
                      {new Date(user.date_joined).toLocaleDateString(dateLocale, { month: "long", year: "numeric" })}
                    </div>
                  )}
                </div>

                {/* ── Action buttons ── */}
                <div className="pp-hero-btns">
                  <button type="button" className="pp-btn pp-btn--solid" onClick={() => setDrawerOpen(true)}>
                    <i className="bi bi-pencil" aria-hidden />{t("edit_profile") || "Edit profile"}
                  </button>
                  <button type="button" className="pp-btn pp-btn--ghost" onClick={copyLink}>
                    <i className="bi bi-link-45deg" aria-hidden />{t("copy_link") || "Copy link"}
                  </button>
                  <button type="button" className="pp-btn pp-btn--icon pp-btn--danger-ghost" onClick={() => setDeleteOpen(true)} aria-label={t("delete_account") || "Delete account"}>
                    <i className="bi bi-trash3" />
                  </button>
                </div>
              </div>

              {/* ── Stats ── */}
              <div className="pp-stats">
                <div className="pp-stat">
                  <span className="pp-stat-n">{savedCount}</span>
                  <span className="pp-stat-l">{t("saved") || "Saved"}</span>
                </div>
                {isCreator && <>
                  <div className="pp-stat-sep" aria-hidden />
                  <div className="pp-stat">
                    <span className="pp-stat-n">{articlesCount}</span>
                    <span className="pp-stat-l">{t("articles") || "Articles"}</span>
                  </div>
                  <div className="pp-stat-sep" aria-hidden />
                  <div className="pp-stat">
                    <span className="pp-stat-n">{activityCount}</span>
                    <span className="pp-stat-l">{t("activity") || "Activity"}</span>
                  </div>
                </>}
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="pp-tabs" role="tablist" aria-label={t("profile_sections") || "Profile sections"}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                role="tab"
                type="button"
                className={`pp-tab${activeTab === tab.key ? " pp-tab--on" : ""}`}
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={`bi ${tab.icon}`} aria-hidden />
                <span>{tab.label}</span>
                <span className="pp-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ══════════════ LAYOUT ══════════════ */}
        <div className="pp-layout">

          {/* ── MAIN COLUMN ── */}
          <div className="pp-main">

            {/* ─── ACTIVITY ─── */}
            {activeTab === "activity" && (
              <section className="pp-card" role="tabpanel" aria-label={t("activity") || "Activity"}>
                <div className="pp-card-hd">
                  <h2 className="pp-card-title">
                    <i className="bi bi-lightning-charge" aria-hidden />
                    {t("recent_activity") || "Recent activity"}
                  </h2>
                </div>
                {activity.length === 0 ? (
  <EmptyState
    icon="bi-lightning"
    title={t("no_activity_yet") || "No activity yet"}
    sub={t("activity_sub") || "Your interactions will show here."}
  />
) : (
  <ul className="pp-activity-list" role="list">
    {activity.map((it, i) => {
      /*
       * ★ Build target URL from every possible field the backend might return.
       *   Works with Django signals, django-activity-stream, custom activity models, etc.
       */
      const slug = it.article_slug || it.slug || it.object_slug || null;
      const aId  = it.article_id   || it.object_id || null;
      const sSlug= it.subject_slug || null;
      const sId  = it.subject_id   || null;

      const targetUrl =
        it.url              ||
        it.link             ||
        it.target_url       ||
        it.object_url       ||
        it.resource_url     ||
        (slug                ? `/articles/${slug}`            : null) ||
        (aId  && !sId        ? `/articles/${aId}`             : null) ||
        (sSlug               ? `/subject/${sSlug}`            : null) ||
        (sId                 ? `/subject/${sId}`              : null) ||
        null;

      const isClickable  = !!targetUrl;

      // ★ Comment ID for delete — try all possible field names
      const commentId =
        it.comment_id ||
        it.comment    ||
        (it.action_type === "comment" ? it.object_id : null) ||
        (it.verb === "commented"      ? it.object_id : null) ||
        null;

      const isComment = !!(commentId || /comment/i.test(it.action || it.verb || ""));

      /*
       * ★ Detail text language:
       *   - Backend should return `detail` in the right lang via Accept-Language.
       *   - If it returns `detail_translations` object → use the right lang.
       *   - Fallback to raw `detail`.
       */
      const detailText =
        (it.detail_translations && (it.detail_translations[lang] || it.detail_translations["en"])) ||
        it[`detail_${lang}`]  ||
        it.detail             ||
        null;

      /*
       * ★ Action text — translate known verbs client-side as extra safety
       */
      const knownVerbs = {
        commented:  { en: "Commented",  fr: "Commenté",  ar: "علّق",    de: "Kommentiert" },
        liked:      { en: "Liked",      fr: "Aimé",      ar: "أعجبه",   de: "Gefällt mir" },
        saved:      { en: "Saved",      fr: "Enregistré",ar: "حفظ",     de: "Gespeichert" },
        shared:     { en: "Shared",     fr: "Partagé",   ar: "شارك",    de: "Geteilt"     },
        published:  { en: "Published",  fr: "Publié",    ar: "نشر",     de: "Veröffentlicht" },
      };
      const verb       = (it.verb || it.action_type || "").toLowerCase();
      const actionText =
        (knownVerbs[verb]?.[lang]) ||
        it[`action_${lang}`]       ||
        it.action                  ||
        it.title                   ||
        (t("action") || "Action");

      const ItemTag   = isClickable ? Link : "li";
      const itemProps = isClickable
        ? { to: targetUrl, className: "pp-activity-item pp-activity-item--link", role: "listitem" }
        : { className: "pp-activity-item", role: "listitem" };

      return (
        <ItemTag key={i} {...itemProps}>
          <div
            className="pp-activity-dot"
            aria-hidden
            style={isClickable ? {} : { background: "var(--nh-ink2)", opacity: .4 }}
          />

          <div className="pp-activity-body">
            {/* Action label */}
            <div className="pp-activity-action">{actionText}</div>

            {/* Detail — shown in user's language */}
            {detailText && (
              <div className="pp-activity-detail">{detailText}</div>
            )}

            {/* Timestamp */}
            {it.date && (
              <time className="pp-activity-time">
                {new Date(it.date).toLocaleString(dateLocale)}
              </time>
            )}

            {/* Bottom row: arrow + delete */}
            <div className="pp-activity-actions-row">
              {isClickable && (
                <span className="pp-activity-go" aria-hidden>
                  <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"}`} />
                  <span>{t("view") || "View"}</span>
                </span>
              )}
              {isComment && commentId && (
                <button
                  type="button"
                  className="pp-activity-del"
                  aria-label={t("delete_comment") || "Delete comment"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteActivityComment(commentId, slug, it.id); 
                  }}
                >
                  <i className="bi bi-trash3" />
                  <span>{t("delete") || "Delete"}</span>
                </button>
              )}
            </div>
          </div>
        </ItemTag>
      );
    })}
  </ul>
)}
</section>
            )}

            {/* ─── SAVED ─── */}
                  {activeTab === "saved" && (
                    <section className="pp-card" role="tabpanel" aria-label={t("saved_subjects") || "Saved subjects"}>
                    <div className="pp-card-hd">
                      <h2 className="pp-card-title">
                      <i className="bi bi-bookmark-heart" aria-hidden />
                      {t("saved_subjects") || "Saved subjects"}
                      </h2>
                      <span className="pp-pill">{savedSubjects.length}</span>
                    </div>
                    {loadingSubjects ? (
                      <LoadingRow label={t("loading_subjects") || "Loading subjects…"} />
                    ) : savedSubjects.length === 0 ? (
                      <EmptyState
                      icon="bi-bookmark"
                      title={t("nothing_saved_yet") || "Nothing saved yet"}
                      sub={t("nothing_saved_sub") || "Bookmark subjects you find interesting."}
                      />
                    ) : (
                      <div className="pp-subjects-grid">
                      {savedSubjects.map(s => <GridCard key={s.id || s.slug} subject={s} />)}
    </div>
                )}
              </section>
            

              )}    
            {activeTab === "articles" && isCreator && (
              <section className="pp-card" role="tabpanel" aria-label={t("my_articles") || "My articles"}>
                <div className="pp-card-hd">
                  <h2 className="pp-card-title">
                    <i className="bi bi-newspaper" aria-hidden />
                    {t("my_articles") || "My articles"}
                  </h2>
                  <div className="pp-card-actions">
                    <Link to="/articles/new" className="pp-btn pp-btn--solid pp-btn--sm">
                      <i className="bi bi-plus-lg" aria-hidden />{t("new_article") || "New article"}
                    </Link>
                    <button type="button" className="pp-btn pp-btn--ghost pp-btn--sm" onClick={exportArticles}>
                      <i className="bi bi-download" aria-hidden />{t("export") || "Export"}
                    </button>
                  </div>
                </div>
                {loadingArticles ? (
                  <LoadingRow label={t("loading_articles") || "Loading articles…"} />
                ) : articles.length === 0 ? (
                  <EmptyState icon="bi-file-earmark-text" title={t("no_articles_yet") || "No articles yet"}>
                    <Link to="/articles/new" className="pp-btn pp-btn--solid" style={{ marginTop: 14 }}>
                      {t("write_first_article") || "Write your first article"}
                    </Link>
                  </EmptyState>
                ) : (
                  <div className="pp-articles-list">
                    {articles.map(a => (
                      <div key={a.id || a.slug} className="pp-article-row">
                        <div className="pp-article-body">
                          <h3 className="pp-article-title">{a.title || `(${t("untitled") || "untitled"})`}</h3>
                          {a.excerpt && (
                            <p className="pp-article-excerpt">
                              {a.excerpt.slice(0, 130)}{a.excerpt.length > 130 ? "…" : ""}
                            </p>
                          )}
                          {a.published && <time className="pp-article-date">{a.published}</time>}
                        </div>
                        <div className="pp-article-acts">
                          <Link to={a.slug ? `/articles/${a.slug}` : "#"} className="pp-btn pp-btn--ghost pp-btn--sm">
                            <i className="bi bi-eye" aria-hidden />{t("read") || "Read"}
                          </Link>
                          <Link to={`/articles/edit/${a.id || a.slug || ""}`} className="pp-btn pp-btn--ghost pp-btn--sm">
                            <i className="bi bi-pencil" aria-hidden />{t("edit") || "Edit"}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>

          {/* ── SIDEBAR ── */}
          <aside className="pp-sidebar" aria-label={t("profile_sidebar") || "Profile sidebar"}>

            {/* Details card */}
            <div className="pp-card pp-card--aside">
              <div className="pp-card-hd">
                <h2 className="pp-card-title">
                  <i className="bi bi-person-circle" aria-hidden />{t("details") || "Details"}
                </h2>
              </div>
              <dl className="pp-details">
                <DetailRow icon="bi-at"        label={t("username") || "Username"} value={`@${user.username}`} />
                <DetailRow icon="bi-envelope"  label={t("email")    || "Email"}    value={user.email || "—"} small />
                {(user.first_name || user.last_name) && (
                  <DetailRow icon="bi-person" label={t("name") || "Name"} value={[user.first_name, user.last_name].filter(Boolean).join(" ")} />
                )}
                <DetailRow
                  icon="bi-calendar3"
                  label={t("joined") || "Joined"}
                  value={user.date_joined ? new Date(user.date_joined).toLocaleDateString(dateLocale) : "—"}
                />
              </dl>
              <div className="pp-aside-actions">
                <button type="button" className="pp-btn pp-btn--solid pp-btn--full" onClick={() => setDrawerOpen(true)}>
                  <i className="bi bi-pencil" aria-hidden />{t("edit_profile") || "Edit profile"}
                </button>
                <button type="button" className="pp-btn pp-btn--ghost pp-btn--full" onClick={copyLink}>
                  <i className="bi bi-link-45deg" aria-hidden />{t("copy_link") || "Copy link"}
                </button>
                <button type="button" className="pp-btn pp-btn--ghost pp-btn--full" onClick={() => refreshProfile(true)}>
                  <i className="bi bi-arrow-clockwise" aria-hidden />{t("refresh") || "Refresh"}
                </button>
                <button type="button" className="pp-btn pp-btn--danger-ghost pp-btn--full" onClick={() => setDeleteOpen(true)}>
                  <i className="bi bi-trash3" aria-hidden />{t("delete_account") || "Delete account"}
                </button>
              </div>
            </div>

            {/* Tips */}
            <div className="pp-card pp-card--aside pp-card--tips">
              <div className="pp-card-hd">
                <h2 className="pp-card-title"><i className="bi bi-lightbulb" aria-hidden />{t("tips") || "Tips"}</h2>
              </div>
              <ul className="pp-tips">
                <li><i className="bi bi-check2-circle" aria-hidden />{t("tip_1") || "PNG or JPG for best avatar quality."}</li>
                <li><i className="bi bi-check2-circle" aria-hidden />{t("tip_2") || "Server avatar replaces preview after save."}</li>
                <li><i className="bi bi-check2-circle" aria-hidden />{t("tip_3") || "Your profile link is publicly shareable."}</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* ══════════════ EDIT DRAWER ══════════════ */}
      <div
        className={`pp-drawer-veil${drawerOpen ? " pp-drawer-veil--on" : ""}`}
        onClick={closeDrawer}
        aria-hidden
      />
      <div
        className={`pp-drawer${drawerOpen ? " pp-drawer--open" : ""}`}
        role="dialog" aria-modal="true" aria-label={t("edit_profile") || "Edit profile"}
      >
        <div className="pp-drawer-hd">
          <h2 className="pp-drawer-title">{t("edit_profile") || "Edit profile"}</h2>
          <button type="button" className="pp-icon-btn" onClick={closeDrawer} aria-label={t("close") || "Close"}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="pp-drawer-body">
          <form onSubmit={submitProfileEdit} noValidate>

            {/* ── Photo ── */}
            <div className="pp-drawer-section">
              <span className="pp-drawer-section-label">{t("photo") || "Photo"}</span>
              <div
                className="pp-drop-zone"
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) handleAvatarFile(f); }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById("pp-file-input")?.click()}
                onKeyDown={(e) => e.key === "Enter" && document.getElementById("pp-file-input")?.click()}
                role="button" tabIndex={0} aria-label={t("upload_avatar") || "Upload avatar"}
              >
                <div className="pp-drop-preview">
                  {(editForm.avatarPreview || avatarSrc)
                    ? <img src={editForm.avatarPreview || avatarSrc} alt="preview" className="pp-drop-img" />
                    : <div className="pp-drop-initials">{initials}</div>
                  }
                  <div className="pp-drop-veil"><i className="bi bi-camera-fill" /></div>
                </div>
                <div className="pp-drop-text">
                  <span className="pp-drop-cta">{t("drop_image_or_click") || "Drop image or click to browse"}</span>
                  <span className="pp-drop-hint">{t("avatar_formats") || "PNG · JPG · WEBP · max 6 MB"}</span>
                </div>
                <input
                  id="pp-file-input" type="file" accept="image/*"
                  className="pp-sr-only"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarFile(f); e.target.value = ""; }}
                />
              </div>
            </div>

            {/* ── Account fields ── */}
            <div className="pp-drawer-section">
              <span className="pp-drawer-section-label">{t("account") || "Account"}</span>
              <Field id="dw-user"  label={t("username")   || "Username"}   type="text"  required value={editForm.username}   onChange={v => setEditForm(f => ({ ...f, username: v }))} />
              <Field id="dw-email" label={t("email")       || "Email"}       type="email"          value={editForm.email}      onChange={v => setEditForm(f => ({ ...f, email: v }))} />
              <div className="pp-field-row">
                <Field id="dw-fn" label={t("first_name") || "First name"} type="text" value={editForm.first_name} onChange={v => setEditForm(f => ({ ...f, first_name: v }))} />
                <Field id="dw-ln" label={t("last_name")  || "Last name"}  type="text" value={editForm.last_name}  onChange={v => setEditForm(f => ({ ...f, last_name: v }))} />
              </div>
            </div>

            {/* ── Language ── */}
          

            {/* ── Change password inline ── */}
            <div className="pp-drawer-section">
              <button
                type="button"
                className="pp-pw-toggle"
                onClick={() => setEditPwOpen(s => !s)}
                aria-expanded={editPwOpen}
              >
                <div className="pp-pw-toggle-l">
                  <i className="bi bi-lock" aria-hidden />
                  <div>
                    <div className="pp-pw-title">{t("change_password") || "Change password"}</div>
                    <div className="pp-pw-hint">{t("change_password_hint") || "Optional — leave closed to keep current"}</div>
                  </div>
                </div>
                <i className={`bi ${editPwOpen ? "bi-chevron-up" : "bi-chevron-down"} pp-pw-chev`} aria-hidden />
              </button>

              {editPwOpen && (
                <div className="pp-pw-panel">
                  <Field id="dw-cpw" label={t("current_password") || "Current password"} type="password" value={editPwForm.current_password} onChange={v => setEditPwForm(p => ({ ...p, current_password: v }))} />
                  <Field id="dw-npw" label={t("new_password")     || "New password"}     type="password" value={editPwForm.new_password}     onChange={v => setEditPwForm(p => ({ ...p, new_password: v }))} required />
                  <Field id="dw-rpw" label={t("repeat_password")  || "Repeat password"}  type="password" value={editPwForm.re_new_password}  onChange={v => setEditPwForm(p => ({ ...p, re_new_password: v }))} required />
                  <div className="pp-pw-actions">
                    <button type="button" className="pp-btn pp-btn--ghost pp-btn--sm"
                      onClick={() => { setEditPwForm({ current_password: "", new_password: "", re_new_password: "" }); setEditPwOpen(false); }}
                      disabled={editPwLoading}>{t("cancel") || "Cancel"}</button>
                    <button type="button" className="pp-btn pp-btn--solid pp-btn--sm"
                      onClick={submitDrawerPw} disabled={editPwLoading}>
                      {editPwLoading
                        ? <><span className="pp-spinner" />{t("saving") || "Saving…"}</>
                        : (t("save_password") || "Save password")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Drawer footer ── */}
            <div className="pp-drawer-ft">
              <button type="button" className="pp-btn pp-btn--ghost" onClick={closeDrawer}>{t("cancel") || "Cancel"}</button>
              <button type="submit" className="pp-btn pp-btn--solid" disabled={savingEdit}>
                {savingEdit
                  ? <><span className="pp-spinner" />{t("saving") || "Saving…"}</>
                  : (t("save_changes") || "Save changes")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ══════════════ MODALS ══════════════ */}
      <ConfirmModal
        show={pwOpen}
        title={t("change_password") || "Change password"}
        confirmText={t("change_password") || "Change password"}
        cancelText={t("cancel") || "Cancel"}
        busy={pwLoading}
        onCancel={() => setPwOpen(false)}
        onConfirm={submitChangePw}
        body={
          <div className="pp-field-stack">
            <Field id="m-npw" label={t("new_password")    || "New password"}    type="password" value={pwForm.new_password}    onChange={v => setPwForm(p => ({ ...p, new_password: v }))}    required />
            <Field id="m-rpw" label={t("repeat_password") || "Repeat password"} type="password" value={pwForm.re_new_password} onChange={v => setPwForm(p => ({ ...p, re_new_password: v }))} required />
            <span className="pp-field-hint">{t("password_min_chars") || "Minimum 6 characters."}</span>
          </div>
        }
      />

      <ConfirmModal
        show={deleteOpen}
        title={t("delete_account") || "Delete account"}
        confirmText={t("delete_my_account") || "Delete my account"}
        cancelText={t("cancel") || "Cancel"}
        busy={deleteLoading}
        danger
        onCancel={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        body={t("delete_account_warning") || "This is permanent — your account and all data will be removed. There is no undo."}
      />
    </div>
  );
}

/* ─────────── Small helpers ─────────── */
function EmptyState({ icon, title, sub, children }) {
  return (
    <div className="pp-empty">
      <div className="pp-empty-icon"><i className={`bi ${icon}`} aria-hidden /></div>
      <p className="pp-empty-title">{title}</p>
      {sub && <p className="pp-empty-sub">{sub}</p>}
      {children}
    </div>
  );
}

function LoadingRow({ label }) {
  return (
    <div className="pp-loading-row">
      <span className="pp-spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

function DetailRow({ icon, label, value, small }) {
  return (
    <div className="pp-detail-row">
      <dt>
        <i className={`bi ${icon}`} aria-hidden />
        {label}
      </dt>
      <dd style={small ? { fontSize: "0.75rem" } : {}}>{value}</dd>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required, hint }) {
  return (
    <div className="pp-field">
      <label htmlFor={id} className="pp-field-lbl">{label}</label>
      <input
        id={id} type={type} className="pp-field-inp"
        value={value} onChange={e => onChange(e.target.value)}
        required={required}
      />
      {hint && <span className="pp-field-hint">{hint}</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STYLES — identical to original, zero changes
═══════════════════════════════════════════════════════════════ */
let _ppCSS = false;
function ProfileStyles() {
  useEffect(() => {
    if (_ppCSS || document.getElementById("pp-css")) return;
    _ppCSS = true;
    const s = document.createElement("style");
    s.id = "pp-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }, []);
  return null;
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

/* ── tokens (mirrors header.jsx) ── */
:root{
  --nh-font:   'Sora','Helvetica Neue',sans-serif;
  --nh-red:    #e63311;
  --nh-red-dk: #c4280d;
  --nh-ink:    #111111;
  --nh-ink2:   #6b7280;
  --nh-bg:     #ffffff;
  --nh-bg2:    #f7f7f7;
  --nh-border: rgba(0,0,0,.08);
  --nh-sh-sm:  0 1px 4px rgba(0,0,0,.06);
  --nh-sh-md:  0 8px 24px rgba(0,0,0,.10);
  --nh-sh-lg:  0 24px 60px rgba(0,0,0,.16);
  --nh-r:      10px;
  --nh-t:      0.18s cubic-bezier(.4,0,.2,1);
}

/* ── root ── */
.pp-root,.pp-root *{box-sizing:border-box;}
.pp-root{
  font-family:var(--nh-font);color:var(--nh-ink);
  background:var(--nh-bg2);min-height:100vh;
}
.pp-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
.pp-container{max-width:1280px;margin:0 auto;padding:0 clamp(14px,4vw,32px) 80px;}

/* mount animation */
.pp-root .pp-hero-card,
.pp-root .pp-layout{opacity:0;transform:translateY(14px);transition:opacity .45s ease,transform .45s ease;}
.pp-root.pp-in .pp-hero-card{opacity:1;transform:none;transition-delay:0ms;}
.pp-root.pp-in .pp-layout  {opacity:1;transform:none;transition-delay:60ms;}

/* ════════ COVER ════════ */
.pp-cover{
  height:clamp(130px,16vw,210px);
  background:var(--nh-ink);
  position:relative;overflow:hidden;
}
.pp-cover-inner{
  position:absolute;inset:0;
  background:linear-gradient(135deg,var(--nh-red) 0%,#111 55%);
}
.pp-cover-dots{
  position:absolute;inset:0;
  background-image:radial-gradient(rgba(255,255,255,.07) 1px,transparent 1px);
  background-size:22px 22px;pointer-events:none;
}

/* ════════ CARDS ════════ */
.pp-card{
  background:var(--nh-bg);border:1px solid var(--nh-border);
  border-radius:var(--nh-r);box-shadow:var(--nh-sh-sm);
  overflow:hidden;margin-bottom:18px;
}
.pp-card--tips{background:var(--nh-bg2);}
.pp-card--aside{margin-bottom:14px;}

/* ════════ HERO CARD ════════ */
.pp-hero-card{
  background:var(--nh-bg);border:1px solid var(--nh-border);
  border-radius:var(--nh-r);box-shadow:var(--nh-sh-sm);
  overflow:hidden;
  margin-top:clamp(-44px,-5.5vw,-28px);
  position:relative;z-index:2;margin-bottom:20px;
}
.pp-hero-accent{
  height:3px;
  background:linear-gradient(to right,var(--nh-red) 80px,var(--nh-border) 80px);
}

.pp-hero-body{
  display:flex;align-items:flex-start;gap:clamp(14px,3vw,28px);
  padding:clamp(16px,3vw,26px) clamp(16px,3vw,26px) 0;
}

/* avatar */
.pp-avatar-wrap{
  width:clamp(76px,9vw,112px);height:clamp(76px,9vw,112px);
  border-radius:12px;overflow:hidden;
  border:3px solid var(--nh-bg);
  box-shadow:var(--nh-sh-md);
  flex-shrink:0;position:relative;
  margin-top:clamp(-28px,-3.5vw,-18px);
  background:var(--nh-bg2);
}
.pp-avatar-img{width:100%;height:100%;object-fit:cover;display:block;}
.pp-avatar-letters{
  width:100%;height:100%;
  background:linear-gradient(135deg,var(--nh-red),#ff6b35);
  color:#fff;font-weight:800;font-size:clamp(1.25rem,2.5vw,2rem);
  display:flex;align-items:center;justify-content:center;
}
.pp-avatar-cam{
  position:absolute;bottom:4px;right:4px;
  width:26px;height:26px;border-radius:6px;
  background:rgba(0,0,0,.65);color:#fff;
  border:none;cursor:pointer;font-size:.75rem;
  display:flex;align-items:center;justify-content:center;
  opacity:0;transition:opacity var(--nh-t);
}
.pp-avatar-wrap:hover .pp-avatar-cam{opacity:1;}

.pp-hero-info{flex:1;min-width:0;}
.pp-hero-top-row{
  display:flex;align-items:flex-start;justify-content:space-between;
  gap:12px;flex-wrap:wrap;margin-bottom:14px;
}
.pp-display-name{
  font-size:clamp(1.125rem,2.8vw,1.625rem);font-weight:800;
  letter-spacing:-.03em;margin:0 0 5px;color:var(--nh-ink);
}
.pp-meta-row{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:5px;}
.pp-at{font-size:.8125rem;color:var(--nh-ink2);font-weight:500;}
.pp-joined{font-size:.72rem;color:var(--nh-ink2);display:flex;align-items:center;gap:5px;}
.pp-joined .bi{color:var(--nh-red);}

/* badges */
.pp-badge{
  display:inline-flex;align-items:center;gap:4px;
  font-size:.6rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  padding:3px 8px;border-radius:3px;
}
.pp-badge--red  {background:rgba(230,51,17,.1); color:var(--nh-red);border:1px solid rgba(230,51,17,.2);}
.pp-badge--green{background:rgba(16,185,129,.1);color:#059669;     border:1px solid rgba(16,185,129,.2);}

.pp-email-chip{
  display:inline-flex;align-items:center;gap:5px;
  font-size:.72rem;font-weight:500;color:var(--nh-ink2);
  background:var(--nh-bg2);border:1px solid var(--nh-border);
  padding:3px 10px;border-radius:100px;
}
.pp-email-chip .bi{color:var(--nh-red);}

/* hero buttons */
.pp-hero-btns{display:flex;align-items:center;gap:7px;flex-wrap:wrap;flex-shrink:0;}

/* stats */
.pp-stats{
  display:flex;align-items:center;gap:18px;flex-wrap:wrap;
  padding:13px 0 18px;border-top:1px solid var(--nh-border);
}
.pp-stat{display:flex;flex-direction:column;align-items:center;}
.pp-stat-n{font-size:1.25rem;font-weight:800;letter-spacing:-.03em;color:var(--nh-ink);line-height:1;}
.pp-stat-l{font-size:.6rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--nh-ink2);margin-top:2px;}
.pp-stat-sep{width:1px;height:30px;background:var(--nh-border);flex-shrink:0;}

/* ════════ TABS ════════ */
.pp-tabs{
  display:flex;border-top:1px solid var(--nh-border);
  overflow-x:auto;scrollbar-width:none;
}
.pp-tabs::-webkit-scrollbar{display:none;}
.pp-tab{
  display:flex;align-items:center;gap:6px;
  padding:12px 20px;background:none;border:none;cursor:pointer;
  font-family:var(--nh-font);font-size:.8125rem;font-weight:600;
  color:var(--nh-ink2);white-space:nowrap;
  position:relative;transition:color var(--nh-t);flex-shrink:0;
}
.pp-tab::after{
  content:'';position:absolute;bottom:0;left:20px;right:20px;
  height:2px;background:var(--nh-red);border-radius:2px 2px 0 0;
  transform:scaleX(0);transform-origin:center;transition:transform var(--nh-t);
}
.pp-tab:hover{color:var(--nh-ink);}
.pp-tab--on{color:var(--nh-red);}
.pp-tab--on::after{transform:scaleX(1);}
.pp-tab .bi{font-size:.9rem;}
.pp-tab-count{
  font-size:.6rem;font-weight:700;
  background:var(--nh-bg2);border:1px solid var(--nh-border);
  padding:2px 7px;border-radius:100px;color:var(--nh-ink2);
  min-width:22px;text-align:center;
}
.pp-tab--on .pp-tab-count{
  background:rgba(230,51,17,.1);border-color:rgba(230,51,17,.2);color:var(--nh-red);
}

/* ════════ LAYOUT ════════ */
.pp-layout{display:grid;grid-template-columns:1fr 290px;gap:18px;align-items:start;}
@media(max-width:1023px){.pp-layout{grid-template-columns:1fr;}}

/* ════════ CARD HEADER ════════ */
.pp-card-hd{
  display:flex;align-items:center;justify-content:space-between;
  padding:16px 20px 0;gap:10px;
}
.pp-card-title{
  font-size:.875rem;font-weight:700;
  display:flex;align-items:center;gap:7px;
  color:var(--nh-ink);margin:0;letter-spacing:-.01em;
}
.pp-card-title .bi{color:var(--nh-red);}
.pp-card-actions{display:flex;gap:6px;}
.pp-pill{
  font-size:.66rem;font-weight:700;
  background:var(--nh-bg2);border:1px solid var(--nh-border);
  padding:3px 9px;border-radius:100px;color:var(--nh-ink2);
}

/* ════════ EMPTY STATE ════════ */
.pp-empty{display:flex;flex-direction:column;align-items:center;padding:52px 24px;text-align:center;}
.pp-empty-icon{
  width:54px;height:54px;border-radius:50%;
  border:2px dashed var(--nh-border);
  display:flex;align-items:center;justify-content:center;
  font-size:1.375rem;color:var(--nh-ink2);margin-bottom:14px;
}
.pp-empty-title{font-weight:700;font-size:.9375rem;margin:0 0 4px;color:var(--nh-ink);}
.pp-empty-sub{font-size:.8125rem;color:var(--nh-ink2);margin:0;}

/* ════════ LOADING ROW ════════ */
.pp-loading-row{display:flex;align-items:center;gap:9px;padding:24px 20px;font-size:.8125rem;color:var(--nh-ink2);}

/* ════════ ACTIVITY ════════ */
.pp-activity-list{list-style:none;padding:14px 20px;margin:0;display:flex;flex-direction:column;}
.pp-activity-item{display:flex;gap:13px;padding:13px 0;border-bottom:1px solid var(--nh-border);}
.pp-activity-item:last-child{border-bottom:none;}
.pp-activity-dot{width:8px;height:8px;border-radius:50%;background:var(--nh-red);flex-shrink:0;margin-top:5px;}
.pp-activity-action{font-size:.875rem;font-weight:600;color:var(--nh-ink);margin-bottom:2px;}
.pp-activity-detail{font-size:.78rem;color:var(--nh-ink2);margin-bottom:2px;}
.pp-activity-time{font-size:.7rem;color:var(--nh-ink2);}

/* ════════ SAVED SUBJECTS ════════ */
.pp-subjects-grid{
  display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));
  gap:12px;padding:14px 20px 20px;
}
.pp-subject-card{
  border:1px solid var(--nh-border);border-radius:var(--nh-r);
  background:var(--nh-bg2);display:flex;flex-direction:column;
  transition:border-color var(--nh-t),box-shadow var(--nh-t),transform .12s;
}
.pp-subject-card:hover{border-color:rgba(230,51,17,.3);box-shadow:var(--nh-sh-md);transform:translateY(-2px);}
.pp-subject-top{padding:14px 14px 6px;flex:1;}
.pp-subject-name{font-size:.875rem;font-weight:700;margin:0 0 5px;color:var(--nh-ink);}
.pp-subject-desc{font-size:.75rem;color:var(--nh-ink2);margin:0;line-height:1.55;}
.pp-subject-foot{padding:8px 14px 12px;}
.pp-subject-link{
  display:inline-flex;align-items:center;gap:5px;
  font-size:.75rem;font-weight:700;color:var(--nh-red);
  text-decoration:none;transition:gap var(--nh-t);
}
.pp-subject-link:hover{gap:8px;}

/* ════════ ARTICLES ════════ */
.pp-articles-list{padding:0 20px 20px;display:flex;flex-direction:column;}
.pp-article-row{
  display:flex;align-items:flex-start;justify-content:space-between;gap:14px;
  padding:15px 0;border-bottom:1px solid var(--nh-border);
}
.pp-article-row:last-child{border-bottom:none;}
.pp-article-body{flex:1;min-width:0;}
.pp-article-title{font-size:.9375rem;font-weight:700;margin:0 0 4px;color:var(--nh-ink);}
.pp-article-excerpt{font-size:.78rem;color:var(--nh-ink2);margin:0 0 4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pp-article-date{font-size:.7rem;color:var(--nh-ink2);}
.pp-article-acts{display:flex;gap:6px;flex-shrink:0;}

/* ════════ SIDEBAR DETAILS ════════ */
.pp-details{padding:10px 20px 14px;margin:0;display:flex;flex-direction:column;}
.pp-detail-row{
  display:flex;justify-content:space-between;align-items:center;gap:10px;
  padding:9px 0;border-bottom:1px solid var(--nh-border);font-size:.8125rem;
}
.pp-detail-row:last-child{border-bottom:none;}
.pp-detail-row dt{
  display:flex;align-items:center;gap:6px;
  color:var(--nh-ink2);font-weight:600;white-space:nowrap;
  font-size:.7rem;text-transform:uppercase;letter-spacing:.04em;
}
.pp-detail-row dt .bi{color:var(--nh-red);font-size:.75rem;}
.pp-detail-row dd{color:var(--nh-ink);font-weight:500;margin:0;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px;}
.pp-aside-actions{padding:0 20px 18px;display:flex;flex-direction:column;gap:7px;}
.pp-tips{list-style:none;padding:10px 20px 18px;margin:0;display:flex;flex-direction:column;gap:8px;}
.pp-tips li{display:flex;align-items:flex-start;gap:8px;font-size:.78rem;color:var(--nh-ink2);}
.pp-tips .bi{color:var(--nh-red);flex-shrink:0;margin-top:1px;}

/* ════════ BUTTONS ════════ */
.pp-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:6px;
  font-family:var(--nh-font);font-size:.8rem;font-weight:700;
  padding:9px 16px;border-radius:8px;cursor:pointer;
  border:1.5px solid transparent;white-space:nowrap;text-decoration:none;
  transition:background var(--nh-t),border-color var(--nh-t),color var(--nh-t),transform .1s;
}
.pp-btn:active:not(:disabled){transform:scale(.97);}
.pp-btn:disabled{opacity:.6;cursor:not-allowed;}
.pp-btn .bi{font-size:.875rem;}
.pp-btn--solid{background:var(--nh-red);color:#fff;border-color:var(--nh-red);}
.pp-btn--solid:hover:not(:disabled){background:var(--nh-red-dk);border-color:var(--nh-red-dk);}
.pp-btn--ghost{background:var(--nh-bg2);color:var(--nh-ink);border-color:var(--nh-border);}
.pp-btn--ghost:hover:not(:disabled){border-color:var(--nh-ink);background:#fff;}
.pp-btn--danger{background:#dc2626;color:#fff;border-color:#dc2626;}
.pp-btn--danger:hover:not(:disabled){background:#b91c1c;}
.pp-btn--danger-ghost{background:var(--nh-bg2);color:#dc2626;border-color:var(--nh-border);}
.pp-btn--danger-ghost:hover:not(:disabled){border-color:#fca5a5;background:#fef2f2;}
.pp-btn--sm{padding:6px 12px;font-size:.74rem;}
.pp-btn--full{width:100%;}
.pp-btn--icon{width:36px;height:36px;padding:0;border-radius:8px;}
.pp-icon-btn{
  width:32px;height:32px;border-radius:7px;border:1px solid var(--nh-border);
  background:var(--nh-bg2);color:var(--nh-ink2);cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:.8rem;
  transition:background var(--nh-t),color var(--nh-t);
}
.pp-icon-btn:hover{background:#ececec;color:var(--nh-ink);}

/* ════════ FORM FIELDS ════════ */
.pp-field{display:flex;flex-direction:column;gap:5px;}
.pp-field-stack{display:flex;flex-direction:column;gap:12px;}
.pp-field-lbl{font-size:.65rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--nh-ink2);}
.pp-field-inp{
  padding:10px 13px;background:var(--nh-bg2);
  border:1.5px solid var(--nh-border);border-radius:8px;
  font-size:.875rem;font-family:var(--nh-font);color:var(--nh-ink);
  outline:none;width:100%;
  transition:border-color var(--nh-t),box-shadow var(--nh-t),background var(--nh-t);
}
.pp-field-inp:focus{background:#fff;border-color:var(--nh-red);box-shadow:0 0 0 3px rgba(230,51,17,.1);}
.pp-field-sel{cursor:pointer;}
.pp-field-hint{font-size:.68rem;color:var(--nh-ink2);}
.pp-field-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;}

/* ════════ DRAWER ════════ */
.pp-drawer-veil{
  position:fixed;inset:0;background:rgba(0,0,0,.5);
  backdrop-filter:blur(3px);z-index:1040;
  opacity:0;pointer-events:none;transition:opacity .26s ease;
}
.pp-drawer-veil--on{opacity:1;pointer-events:all;}
.pp-drawer{
  position:fixed;top:0;bottom:0;right:0;
  width:min(480px,96vw);background:var(--nh-bg);z-index:1050;
  display:flex;flex-direction:column;box-shadow:var(--nh-sh-lg);
  transform:translateX(108%);transition:transform .3s cubic-bezier(.4,0,.2,1);
  overflow:hidden;
}
.pp-drawer--open{transform:translateX(0);}
.pp-drawer-hd{
  display:flex;align-items:center;justify-content:space-between;
  padding:16px 20px;border-bottom:1px solid var(--nh-border);flex-shrink:0;
  background:var(--nh-bg);
}
.pp-drawer-title{font-size:1rem;font-weight:800;letter-spacing:-.02em;margin:0;}
.pp-drawer-body{flex:1;overflow-y:auto;overscroll-behavior:contain;}
.pp-drawer-body::-webkit-scrollbar{width:3px;}
.pp-drawer-body::-webkit-scrollbar-thumb{background:#ddd;border-radius:3px;}
.pp-drawer-section{
  padding:18px 20px;border-bottom:1px solid var(--nh-border);
  display:flex;flex-direction:column;gap:12px;
}
.pp-drawer-section-label{
  font-size:.62rem;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--nh-ink2);
}
.pp-drawer-ft{
  display:flex;justify-content:flex-end;gap:8px;
  padding:16px 20px;border-top:1px solid var(--nh-border);
  background:var(--nh-bg2);flex-shrink:0;
}

/* avatar drop zone */
.pp-drop-zone{
  display:flex;align-items:center;gap:14px;
  border:2px dashed var(--nh-border);border-radius:var(--nh-r);
  padding:16px;cursor:pointer;background:var(--nh-bg2);
  transition:border-color var(--nh-t),background var(--nh-t);
}
.pp-drop-zone:hover,.pp-drop-zone:focus{border-color:rgba(230,51,17,.4);background:#fef2f2;outline:none;}
.pp-drop-preview{
  width:70px;height:70px;border-radius:10px;overflow:hidden;flex-shrink:0;
  border:1px solid var(--nh-border);position:relative;
}
.pp-drop-img{width:100%;height:100%;object-fit:cover;display:block;}
.pp-drop-initials{
  width:100%;height:100%;
  background:linear-gradient(135deg,var(--nh-red),#ff6b35);
  color:#fff;font-weight:800;font-size:1.25rem;
  display:flex;align-items:center;justify-content:center;
}
.pp-drop-veil{
  position:absolute;inset:0;background:rgba(0,0,0,.45);
  display:flex;align-items:center;justify-content:center;
  color:#fff;font-size:1.2rem;opacity:0;transition:opacity var(--nh-t);
}
.pp-drop-zone:hover .pp-drop-veil,.pp-drop-zone:focus .pp-drop-veil{opacity:1;}
.pp-drop-text{display:flex;flex-direction:column;gap:3px;}
.pp-drop-cta{font-size:.8125rem;font-weight:700;color:var(--nh-ink);}
.pp-drop-hint{font-size:.7rem;color:var(--nh-ink2);}

/* password panel */
.pp-pw-toggle{
  display:flex;align-items:center;justify-content:space-between;width:100%;
  background:var(--nh-bg2);border:1px solid var(--nh-border);
  border-radius:8px;padding:12px 14px;cursor:pointer;
  font-family:var(--nh-font);transition:border-color var(--nh-t),background var(--nh-t);
}
.pp-pw-toggle:hover{border-color:var(--nh-ink);background:#fff;}
.pp-pw-toggle-l{display:flex;align-items:center;gap:10px;}
.pp-pw-toggle-l .bi{color:var(--nh-red);font-size:1rem;}
.pp-pw-title{font-size:.875rem;font-weight:700;text-align:left;}
.pp-pw-hint{font-size:.7rem;color:var(--nh-ink2);text-align:left;}
.pp-pw-chev{color:var(--nh-ink2);font-size:.65rem;}
.pp-pw-panel{
  background:var(--nh-bg2);border:1px solid var(--nh-border);
  border-radius:8px;padding:14px;
  display:flex;flex-direction:column;gap:10px;
  animation:pp-fade-down .18s ease;
}
@keyframes pp-fade-down{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.pp-pw-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:4px;}

/* ════════ MODAL ════════ */
.pp-backdrop{
  position:fixed;inset:0;background:rgba(0,0,0,.55);
  backdrop-filter:blur(4px);z-index:1400;
  display:flex;align-items:center;justify-content:center;padding:20px;
}
.pp-modal{
  background:var(--nh-bg);border:1px solid var(--nh-border);
  border-radius:var(--nh-r);box-shadow:var(--nh-sh-lg);
  width:100%;max-width:480px;
  animation:pp-modal-in .2s cubic-bezier(.4,0,.2,1);
}
@keyframes pp-modal-in{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:none}}
.pp-modal-head{
  display:flex;align-items:center;justify-content:space-between;
  padding:18px 20px;border-bottom:1px solid var(--nh-border);
}
.pp-modal-title{font-size:1rem;font-weight:800;margin:0;letter-spacing:-.02em;}
.pp-modal-body{padding:20px;}
.pp-modal-text{font-size:.875rem;color:var(--nh-ink2);margin:0;line-height:1.65;}
.pp-modal-foot{
  display:flex;justify-content:flex-end;gap:8px;
  padding:14px 20px;border-top:1px solid var(--nh-border);
  background:var(--nh-bg2);
}

/* ════════ TOAST ════════ */
.pp-toast-wrap{
  position:fixed;bottom:24px;right:24px;z-index:1500;
  animation:pp-slide-up .22s ease;
}
@keyframes pp-slide-up{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.pp-toast{
  display:flex;align-items:center;gap:10px;
  padding:13px 16px;border-radius:var(--nh-r);
  font-size:.8125rem;font-weight:600;font-family:var(--nh-font);
  box-shadow:var(--nh-sh-lg);max-width:360px;
}
.pp-toast--ok{background:#0f0f0e;color:#fff;border:1px solid rgba(255,255,255,.1);}
.pp-toast--ok  .bi{color:#4ade80;flex-shrink:0;}
.pp-toast--err{background:#1a0808;color:#fecaca;border:1px solid rgba(239,68,68,.2);}
.pp-toast--err .bi{color:#f87171;flex-shrink:0;}
.pp-toast-close{
  background:none;border:none;cursor:pointer;
  color:inherit;opacity:.55;padding:0;margin-left:4px;
  display:flex;font-size:.9rem;
  transition:opacity var(--nh-t);
}
.pp-toast-close:hover{opacity:1;}

/* ════════ BACK TO TOP ════════ */
.pp-btt{
  position:fixed;bottom:28px;right:28px;
  width:40px;height:40px;border-radius:10px;
  background:var(--nh-red);color:#fff;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  font-size:.9rem;box-shadow:var(--nh-sh-md);
  opacity:0;transform:translateY(12px);pointer-events:none;
  transition:opacity var(--nh-t),transform var(--nh-t),background var(--nh-t);
  z-index:900;
}
.pp-btt--on{opacity:1;transform:none;pointer-events:all;}
.pp-btt:hover{background:var(--nh-red-dk);}
.pp-btt:active{transform:scale(.93);}

/* ════════ SPINNER ════════ */
.pp-spinner{
  width:14px;height:14px;border-radius:50%;flex-shrink:0;
  border:2px solid rgba(0,0,0,.12);border-top-color:currentColor;
  animation:pp-spin .65s linear infinite;display:inline-block;
}
.pp-btn--solid .pp-spinner,.pp-btn--danger .pp-spinner{border-color:rgba(255,255,255,.3);border-top-color:#fff;}
@keyframes pp-spin{to{transform:rotate(360deg)}}

/* ════════ SKELETON ════════ */
@keyframes pp-sk{0%{background-position:-600px 0}100%{background-position:600px 0}}
.pp-sk{
  background:linear-gradient(90deg,#ebebeb 25%,#f5f5f5 50%,#ebebeb 75%);
  background-size:1200px 100%;
  animation:pp-sk 1.6s ease-in-out infinite;
  border-radius:6px;
}
.pp-sk--avatar{width:112px;height:112px;border-radius:12px;flex-shrink:0;}

/* ════════ RESPONSIVE ════════ */
@media(max-width:767px){
  .pp-hero-body{flex-direction:column;}
  .pp-avatar-wrap{width:80px;height:80px;margin-top:-30px;}
  .pp-hero-top-row{flex-direction:column;gap:14px;}
  .pp-hero-btns{width:100%;}
  .pp-hero-btns .pp-btn:not(.pp-btn--icon){flex:1;}
  .pp-email-chip{display:none;}
  .pp-toast-wrap{bottom:14px;right:12px;left:12px;}
  .pp-toast{max-width:100%;}
  .pp-btt{bottom:18px;right:14px;}
}
@media(max-width:479px){
  .pp-subjects-grid{grid-template-columns:1fr;}
  .pp-article-row{flex-direction:column;gap:10px;}
  .pp-field-row{grid-template-columns:1fr;}
  .pp-cover{height:100px;}
  .pp-hero-btns .pp-btn--icon{display:none;}
}

/* ════════ DARK MODE ════════ */
@media(prefers-color-scheme:dark){
  :root{
    --nh-ink:#f1f1f1;--nh-ink2:#9ca3af;
    --nh-bg:#111;--nh-bg2:#1a1a1a;
    --nh-border:rgba(255,255,255,.09);
  }
  .pp-root{background:#0a0a09;}
  .pp-sk{background:linear-gradient(90deg,#1e1e1c 25%,#2a2a28 50%,#1e1e1c 75%);background-size:1200px 100%;}
  .pp-drop-zone:hover,.pp-drop-zone:focus{background:#1e0f0f;}
  .pp-subject-card{background:#1a1a1a;}
  .pp-field-inp{background:#1a1a1a;color:#f1f1f1;}
  .pp-field-inp:focus{background:#111;}
  .pp-pw-toggle{background:#1a1a1a;}
  .pp-pw-toggle:hover{background:#222;}
  .pp-pw-panel{background:#1a1a1a;}
  .pp-card--tips{background:#161614;}
  .pp-modal,.pp-drawer{background:#111;}
  .pp-modal-foot,.pp-drawer-ft{background:#0a0a09;}
}

/* ════════ RTL ════════ */
[dir="rtl"] .pp-drawer{right:auto;left:0;transform:translateX(-108%);}
[dir="rtl"] .pp-drawer--open{transform:translateX(0);}
[dir="rtl"] .pp-detail-row dd{text-align:left;}
[dir="rtl"] .pp-toast-wrap{right:auto;left:24px;}
[dir="rtl"] .pp-btt{right:auto;left:28px;}
[dir="rtl"] .pp-drawer-ft{justify-content:flex-start;}
[dir="rtl"] .pp-modal-foot{justify-content:flex-start;}
[dir="rtl"] .pp-hero-accent{background:linear-gradient(to left,var(--nh-red) 80px,var(--nh-border) 80px);}
[dir="rtl"] .pp-subject-link .bi{transform:scaleX(-1);}
[dir="rtl"] .pp-pw-title,[dir="rtl"] .pp-pw-hint{text-align:right;}
@media(max-width:767px){
  [dir="rtl"] .pp-toast-wrap{right:12px;left:12px;}
}
/* bottom actions row inside activity item */
.pp-activity-actions-row{
  display:flex;align-items:center;gap:10px;margin-top:5px;flex-wrap:wrap;
}
.pp-activity-go{
  display:inline-flex;align-items:center;gap:4px;
  font-size:.72rem;font-weight:700;color:var(--nh-red);
  opacity:0;transform:translateX(-4px);
  transition:opacity var(--nh-t),transform var(--nh-t);
  text-decoration:none;
}
.pp-activity-item--link:hover .pp-activity-go{
  opacity:1;transform:translateX(0);
}
/* delete button inside activity */
.pp-activity-del{
  display:inline-flex;align-items:center;gap:4px;
  font-size:.72rem;font-weight:600;color:var(--nh-ink2);
  background:none;border:none;cursor:pointer;
  padding:0;font-family:var(--nh-font);
  transition:color var(--nh-t);
  opacity:0;
}
.pp-activity-item:hover .pp-activity-del,
.pp-activity-item--link:hover .pp-activity-del{
  opacity:1;
}
.pp-activity-del:hover{color:#dc2626;}
[dir="rtl"] .pp-activity-go{transform:translateX(4px);}
[dir="rtl"] .pp-activity-item--link:hover .pp-activity-go{opacity:1;transform:translateX(0);}
/* ════════ PRINT ════════ */
@media print{
  .pp-drawer,.pp-drawer-veil,.pp-toast-wrap,.pp-hero-btns,.pp-tabs,.pp-aside-actions,.pp-btt{display:none;}
  .pp-cover{height:50px;}
  .pp-root{background:#fff;}
}
`;